import {createContext, ReactNode, useContext} from 'react';
import {getBackendUrl} from 'utilities';
import {useMediaCreate, useMediaImport} from 'views/news/hooks';

export const RICH_TEXT_MEDIA_UPLOAD_SIZE_LIMIT_BYTES = 6 * 1024 * 1024;
export const RICH_TEXT_VIDEO_UPLOAD_SIZE_LIMIT_BYTES = 200 * 1024 * 1024;

export type RichTextMediaUpload = {
  uploadMedia: ({path, file}: {path: string; file: File}) => Promise<string>;
  getMediaUrl: (asset: string) => string;
  maxFileSizeBytes?: number;
  /** Size cap for video files; only consulted on surfaces with video uploads enabled. */
  maxVideoFileSizeBytes?: number;
  /**
   * Copies an externally hosted image (e.g. from a pasted Paper doc) into this surface's media
   * storage and resolves to the stored asset. Surfaces without it keep pasted image URLs as-is.
   */
  importMediaFromUrl?: (url: string) => Promise<string>;
};

export const isWithinMediaUploadLimit = (file: File, mediaUpload: RichTextMediaUpload) => {
  const limit = file.type.startsWith('video/')
    ? (mediaUpload.maxVideoFileSizeBytes ?? RICH_TEXT_VIDEO_UPLOAD_SIZE_LIMIT_BYTES)
    : (mediaUpload.maxFileSizeBytes ?? RICH_TEXT_MEDIA_UPLOAD_SIZE_LIMIT_BYTES);
  return file.size <= limit;
};

const MediaUploadContext = createContext<RichTextMediaUpload | undefined>(undefined);

export const MediaUploadProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  value?: RichTextMediaUpload;
}) => <MediaUploadContext.Provider value={value}>{children}</MediaUploadContext.Provider>;

export const useRichTextMediaUpload = () => {
  const scopedMediaUpload = useContext(MediaUploadContext);
  const {uploadMedia} = useMediaCreate();
  const {importMedia} = useMediaImport();

  return (
    scopedMediaUpload ?? {
      uploadMedia,
      getMediaUrl: (asset: string) => `${getBackendUrl()}/api/v1/news/media/${asset}`,
      maxFileSizeBytes: RICH_TEXT_MEDIA_UPLOAD_SIZE_LIMIT_BYTES,
      maxVideoFileSizeBytes: RICH_TEXT_VIDEO_UPLOAD_SIZE_LIMIT_BYTES,
      importMediaFromUrl: importMedia,
    }
  );
};
